// src/pages/leads/LeadRow.jsx
import React from 'react';
import { Tr, Td, Text, Badge } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import LeadActionsMenu from './LeadActionsMenu';
import { computeStage, stageColor, formatAppt, formatTs } from './utils';

/**
 * LeadRow
 * Presentational row for the Leads table. No writes here — just renders cells
 * and calls the callbacks you pass in.
 *
 * Props:
 * - lead: object
 * - onEditCustomer: () => void
 * - onAssign: () => void
 * - onUnassign: () => void
 * - onOpenDnc: () => void
 * - onOpenCancelAppt: () => void
 * - onOpenCancelSale: () => void
 * - onReinstate: () => void
 * - canCancelAppt: boolean
 * - canCancelSale: boolean
 * - canReinstate: boolean
 */

function statusColorScheme(status) {
  const t = (status || '').toUpperCase();
  if (t === 'SOLD') return 'green';
  if (t === 'ASSIGNED') return 'gray';
  if (t === 'PM') return 'blue';
  if (t === 'PTA') return 'purple';
  if (t === 'BO') return 'orange';
  if (t === 'NCI') return 'red';
  if (t === 'CPTA') return 'purple';      // Cancelled Pending To Appoint
  if (t === 'ARCHIVE_DNC') return 'blackAlpha'; // normally hidden upstream
  if (t === 'CANCELLED') return 'blackAlpha';   // legacy
  return 'gray';
}

export default function LeadRow({
  lead,
  onEditCustomer,
  onAssign,
  onUnassign,
  onOpenDnc,
  onOpenCancelAppt,
  onOpenCancelSale,
  onReinstate,
  canCancelAppt,
  canCancelSale,
  canReinstate,
}) {
  const navigate = useNavigate();

  const displayName = lead.customerTitle
    ? `${lead.customerTitle} ${
        lead.customerFullName ||
        `${lead.customerFirstName || ''} ${lead.customerLastName || ''}`.trim()
      }`
    : lead.customerFullName ||
      `${lead.customerFirstName || ''} ${lead.customerLastName || ''}`.trim();

  const stage = computeStage(lead);

  // NEW: navigate to Admin Sales Report for this lead
  const handleOpen = () => {
    if (!lead?.id) return;
    navigate(`/reports/sales-report?leadId=${lead.id}&back=/leads`);
  };

  return (
    <Tr>
      <Td>
        <Text fontWeight="semibold">{displayName}</Text>
        {(lead.adminNotes || lead.sellerBrief) && (
          <Text fontSize="sm" color="gray.600" noOfLines={1}>
            {lead.adminNotes || lead.sellerBrief}
          </Text>
        )}
      </Td>

      <Td>
        {lead.customerPhone ? (
          <a href={`tel:${lead.customerPhone}`}>{lead.customerPhone}</a>
        ) : (
          '—'
        )}
      </Td>

      <Td>{lead.postcode || '—'}</Td>

      <Td>
        <Badge colorScheme={statusColorScheme(lead.status)} variant="subtle">
          {(lead.status || '').toUpperCase()}
        </Badge>
      </Td>

      <Td>
        <Badge colorScheme={stageColor(stage)} fontWeight="semibold">
          {stage}
        </Badge>
      </Td>

      <Td>
        <Text>{lead.salespersonName || '—'}</Text>
      </Td>

      <Td>
        <Text>{formatAppt(lead.appointment) || 'No appointment'}</Text>
        <Text fontSize="xs" color="gray.600">
          Created: {formatTs(lead.createdAt)}
        </Text>
      </Td>

      <Td>
        <LeadActionsMenu
          lead={lead}
          disabledAll={!!lead.apptStartAt && !lead.apptEndAt}
          onOpen={handleOpen}            // NEW
          onEditCustomer={onEditCustomer}
          onAssign={onAssign}
          onUnassign={onUnassign}
          onOpenDnc={onOpenDnc}
          onOpenCancelAppt={onOpenCancelAppt}
          onOpenCancelSale={onOpenCancelSale}
          onReinstate={onReinstate}
          canCancelAppt={canCancelAppt}
          canCancelSale={canCancelSale}
          canReinstate={canReinstate}
        />
      </Td>
    </Tr>
  );
}
